import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, PriceBlockUs } from '@hpstellar/core/visId';
import { Telephone } from '@hpstellar/icons';

import { CheckoutButtonContainer, disableCheckout } from '.';
import useCheckoutCTA, { CTA_TYPES } from './useCheckoutCTA';
import { useBreakpoint } from '../../../../hooks/useDeviceInfo';
import useCartState from '../../../../hooks/useCartState';
import { Helpers } from '../../../../core';
import { scrollToError, BLOCK_ERROR_CLASS } from '../../cart-items/useAlerts';

import './mobile-checkout.less';

const HEADER_PROPS = {
    style: {
        borderBottom: 'none',
    },
};

const ContactCustomerService = ({ children, priceComponent, checkoutButtonProps }) => {
    const { cartInfo } = useCartState();
    const [hasCartBlockingErrors, setHasCartBlockingErrors] = useState(false);
    const { cartDetails, ctoVerification = {} } = cartInfo || {};
    const { cartLevelErrorMessages = [] } = cartDetails || {};
    const {
        disabled,
        contactCC,
        ctaText = 'Complete Order',
    } = disableCheckout(cartLevelErrorMessages, ctoVerification);

    useEffect(() => {
        if (disabled && document.querySelectorAll(`.${BLOCK_ERROR_CLASS}`).length > 0) {
            setHasCartBlockingErrors(disabled);
        } else {
            setHasCartBlockingErrors(false);
        }
    }, [disabled]);

    if (disabled) {
        return (
            <div className="contact-customer-service">
                {priceComponent}
                <Button {...checkoutButtonProps} onClick={scrollToError} />
            </div>
        );
    }

    if (disabled && contactCC) {
        return (
            <div className="contact-customer-service">
                {priceComponent}
                <Button
                    className="cc-checkout-help"
                    variant="footnotes"
                    to={`tel:${contactCC}`}
                    endIcon={<Telephone size="m" />}
                    onClick={scrollToError}
                >
                    {ctaText}
                </Button>
            </div>
        );
    } else {
        return children;
    }
};

export default ({ buttonProps = { children: 'Checkout' }, title = 'Begin Checkout', headerProps = HEADER_PROPS }) => {
    const { cartInfo, emptyCart } = useCartState();
    const { cartView } = cartInfo || {};
    const { grandTotal } = cartView || {};
    const { ctaType, checkoutUrl } = useCheckoutCTA();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const inMobileRange = useBreakpoint([0, 768]);
    const openModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);
    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);
    if (!inMobileRange || emptyCart) {
        return null;
    }

    const checkoutButtonProps = {
        ...buttonProps,
        className: 'mobile-checkout-button',
        children: 'Checkout',
        fullWidth: true,
    };

    const priceComponent = grandTotal ? (
        <PriceBlockUs
            className="mobile-cart-total"
            slim
            finalPrice={Helpers.formatCurrency(grandTotal, undefined, true)}
        />
    ) : (
        <div className="mobile-cart-total" />
    );

    return (
        <>
            <div className="mobile-checkout">
                <ContactCustomerService priceComponent={priceComponent} checkoutButtonProps={checkoutButtonProps}>
                    {priceComponent}
                    <Button
                        {...checkoutButtonProps}
                        to={ctaType === CTA_TYPES.CHECKOUT ? checkoutUrl : undefined}
                        onClick={ctaType === CTA_TYPES.AUTH ? openModal : undefined}
                    />
                </ContactCustomerService>
            </div>
            <Modal
                isOpen={isModalOpen}
                onClose={closeModal}
                verticalAlignment="bottom"
                title={title}
                headerProps={headerProps}
            >
                <div className="mobile-checkout-modal">
                    <PriceBlockUs
                        slim
                        finalPrice={Helpers.formatCurrency(grandTotal, undefined, true)}
                        finalPriceTypographyVariant="subtitle"
                    />
                    <CheckoutButtonContainer />
                </div>
            </Modal>
        </>
    );
};
